import React, { useEffect, useState } from "react";
import { X, FileText, FileSpreadsheet, Calendar } from "lucide-react";
import DatePicker from "../../../../components/DatePicker";
import { getStatusBadgeStyle, getStatusText } from "../../utils/statusUtils";
import {
  dataRecords,
  historyTotals,
  formatMinutes,
  exportHistoryToExcel,
  exportHistoryToPDF,
} from "../../utils/historyExport";

const HistoryModal = ({
  theme,
  isRTL,
  t,
  language,
  showHistoryModal,
  setShowHistoryModal,
  employeeHistory,
  selectedEmployee,
  selectedEmployeeId,
  // period picker + export (all optional so older callers keep working)
  historyRange,
  onApplyPeriod,
  historyLoading = false,
  maxDate,
  showAlert,
}) => {
  // Draft dates live here until "Apply" refetches; they re-sync whenever the
  // parent opens the modal with a new range.
  const [fromDraft, setFromDraft] = useState(historyRange?.from || "");
  const [toDraft, setToDraft] = useState(historyRange?.to || "");
  const [exporting, setExporting] = useState(null); // "pdf" | "excel" | null

  useEffect(() => {
    setFromDraft(historyRange?.from || "");
    setToDraft(historyRange?.to || "");
  }, [historyRange?.from, historyRange?.to, showHistoryModal]);

  if (!showHistoryModal) return null;

  const isDark = theme === "dark";
  const records = dataRecords(employeeHistory);
  const totals = historyTotals(records);
  const periodValid = !!fromDraft && !!toDraft && fromDraft <= toDraft;
  const periodDirty = fromDraft !== (historyRange?.from || "") || toDraft !== (historyRange?.to || "");

  const handleApply = () => {
    if (!periodValid) {
      showAlert?.(language === "ar" ? "خطأ" : "Error", t.invalidPeriod, "error");
      return;
    }
    onApplyPeriod?.(fromDraft, toDraft);
  };

  const handleExport = async (kind) => {
    if (records.length === 0) {
      showAlert?.(language === "ar" ? "تنبيه" : "Notice", t.nothingToExport, "warning");
      return;
    }
    setExporting(kind);
    try {
      const payload = {
        records,
        t,
        language,
        isRTL,
        employeeName: selectedEmployee,
        employeeId: selectedEmployeeId,
        from: historyRange?.from || fromDraft,
        to: historyRange?.to || toDraft,
        getStatusText,
      };
      if (kind === "pdf") await exportHistoryToPDF(payload);
      else await exportHistoryToExcel(payload);
      showAlert?.(language === "ar" ? "نجاح" : "Success", t.exportSuccess, "success");
    } catch (err) {
      console.error("history export failed:", err);
      showAlert?.(language === "ar" ? "خطأ" : "Error", `${t.exportFailed}: ${err?.message || ""}`, "error");
    } finally {
      setExporting(null);
    }
  };

  const toolbarBtn = (variant, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "38px",
    padding: "0 14px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    whiteSpace: "nowrap",
    transition: "all .2s ease",
    border:
      variant === "primary"
        ? "1px solid #3b82f6"
        : `1px solid ${isDark ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.5)"}`,
    background:
      variant === "primary"
        ? "#3b82f6"
        : isDark
          ? "rgba(15,23,42,0.5)"
          : "rgba(248,250,252,0.9)",
    color: variant === "primary" ? "#fff" : isDark ? "#f1f5f9" : "#1e293b",
  });

  const totalCard = (label, value, rgb) => (
    <div
      style={{
        padding: "8px 12px",
        backgroundColor: isDark ? `rgba(${rgb}, 0.15)` : `rgba(${rgb}, 0.1)`,
        borderRadius: "6px",
        border: isDark ? `1px solid rgba(${rgb}, 0.3)` : `1px solid rgba(${rgb}, 0.2)`,
        minWidth: "150px",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: "600", color: isDark ? "rgba(255,255,255,0.7)" : "#666", marginBottom: "2px" }}>
        {label}
      </div>
      <div style={{ fontSize: "16px", fontWeight: "700", color: `rgb(${rgb})` }}>{value}</div>
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
        direction: isRTL ? "rtl" : "ltr",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        WebkitOverflowScrolling: "touch",
      }}
      onClick={() => setShowHistoryModal(false)}
    >
      <div
        style={{
          background: isDark ? "#1e293b" : "#fff",
          borderRadius: "20px",
          width: "min(90vw, 1200px)",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.6)" : "0 20px 60px rgba(0,0,0,0.2)",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "24px" }}>
          {/* ── Header: title, period totals, close ─────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
              marginBottom: "16px",
              borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
              paddingBottom: "16px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 12px 0",
                  textAlign: isRTL ? "right" : "left",
                  color: isDark ? "#fff" : "#0f172a",
                  fontSize: "clamp(18px, 2.4vw, 24px)",
                  fontWeight: "700",
                }}
              >
                {t.attendanceHistory} - {selectedEmployee} - ID: {selectedEmployeeId}
              </h2>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {totalCard(t.periodOvertimeTotal, formatMinutes(totals.overtime, language) === "-" ? "0h 00m" : formatMinutes(totals.overtime, language), "34, 197, 94")}
                {totalCard(t.periodTardinessTotal, formatMinutes(totals.undertime, language) === "-" ? "0h 00m" : formatMinutes(totals.undertime, language), "239, 68, 68")}
                {totalCard(t.totalRecords, totals.days, "59, 130, 246")}
              </div>
            </div>
            <button
              onClick={() => setShowHistoryModal(false)}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: isDark ? "#fff" : "#374151",
                transition: "background 0.2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <X size={24} />
            </button>
          </div>

          {/* ── Period picker + downloads ───────────────────────────────── */}
          <div
            className="nx-history-toolbar"
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: isDark ? "#cbd5e1" : "#475569", alignSelf: "center", paddingTop: "22px" }}>
              <Calendar size={16} />
              {t.historyPeriod}
            </div>
            <div style={{ flex: "1 1 170px", minWidth: "150px", maxWidth: "230px" }}>
              <DatePicker theme={theme} isRTL={isRTL} label={t.fromDate} value={fromDraft} onChange={setFromDraft} maxDate={toDraft || maxDate} dropDirection="down" />
            </div>
            <div style={{ flex: "1 1 170px", minWidth: "150px", maxWidth: "230px" }}>
              <DatePicker theme={theme} isRTL={isRTL} label={t.toDate} value={toDraft} onChange={setToDraft} minDate={fromDraft || undefined} maxDate={maxDate} dropDirection="down" />
            </div>
            <button
              type="button"
              onClick={handleApply}
              disabled={historyLoading || !periodValid}
              style={toolbarBtn(periodDirty ? "primary" : "neutral", historyLoading || !periodValid)}
              title={t.applyPeriod}
            >
              {historyLoading ? "…" : t.applyPeriod}
            </button>
            <div style={{ flex: "1 1 auto" }} />
            <button
              type="button"
              onClick={() => handleExport("pdf")}
              disabled={!!exporting || historyLoading || periodDirty}
              style={toolbarBtn("neutral", !!exporting || historyLoading || periodDirty)}
              title={periodDirty ? t.applyPeriod : t.downloadPDF}
            >
              <FileText size={16} />
              {exporting === "pdf" ? t.exporting : t.downloadPDF}
            </button>
            <button
              type="button"
              onClick={() => handleExport("excel")}
              disabled={!!exporting || historyLoading || periodDirty}
              style={toolbarBtn("neutral", !!exporting || historyLoading || periodDirty)}
              title={periodDirty ? t.applyPeriod : t.downloadExcel}
            >
              <FileSpreadsheet size={16} />
              {exporting === "excel" ? t.exporting : t.downloadExcel}
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0",
                direction: isRTL ? "rtl" : "ltr",
                minWidth: "600px",
              }}
            >
              <thead>
                <tr>
                  {[t.date, t.checkIn, t.checkOut, t.status, t.hoursWorked, t.overtime, t.tardiness].map((header, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "700",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        borderBottom: isDark ? "2px solid rgba(59, 130, 246, 0.3)" : "2px solid rgba(59, 130, 246, 0.1)",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backdropFilter: "blur(0.625rem)",
                        boxShadow: isDark
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "28px", textAlign: "center", color: isDark ? "rgba(255,255,255,0.6)" : "#64748b", fontSize: "14px" }}>
                      {historyLoading ? "…" : t.noRecordsFound}
                    </td>
                  </tr>
                )}
                {records.map((rec, idx) => {
                  const cell = {
                    padding: "14px 16px",
                    textAlign: "center",
                    fontSize: "13px",
                    color: isDark ? "rgba(255,255,255,0.85)" : "#475569",
                    borderBottom: isDark ? "1px solid rgba(59, 130, 246, 0.1)" : "1px solid rgba(59, 130, 246, 0.05)",
                  };
                  const mono = { ...cell, direction: "ltr", fontFamily: "'Courier New', monospace" };
                  return (
                    <tr
                      key={rec.id ?? idx}
                      style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.04)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={cell}>
                        {new Date(rec.date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </td>
                      <td style={mono}>
                        {rec.checkIn ? new Date(rec.checkIn).toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", { hour12: false, hour: "2-digit", minute: "2-digit" }) : "-"}
                      </td>
                      <td style={mono}>
                        {rec.checkOut ? new Date(rec.checkOut).toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", { hour12: false, hour: "2-digit", minute: "2-digit" }) : "-"}
                      </td>
                      <td style={cell}>
                        <span style={getStatusBadgeStyle(rec.status)}>{getStatusText(rec.status, language)}</span>
                      </td>
                      <td style={{ ...cell, fontWeight: "500" }}>
                        {rec.workingHours ? `${Math.round(parseFloat(rec.workingHours) * 10) / 10} ${language === "ar" ? "س" : "h"}` : "-"}
                      </td>
                      <td style={{ ...mono, color: rec.overTime ? "rgb(34, 197, 94)" : cell.color }}>{formatMinutes(rec.overTime, language)}</td>
                      <td style={{ ...mono, color: rec.underTime ? "rgb(239, 68, 68)" : cell.color }}>{formatMinutes(rec.underTime, language)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
